import { useEffect } from 'react';
import { LandingHeader } from '@/components/landing/LandingHeader';
import { LandingFooter } from '@/components/landing/LandingFooter';
import { ScrollToTopButton } from '@/components/landing/ScrollToTopButton';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <LandingHeader />
      
      {/* Hero Section */}
      <section className="py-20 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Política de Privacidade
          </h1>
          <p className="text-muted-foreground">
            Última atualização: Janeiro de 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="space-y-8">
            {/* Introdução */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">1. Introdução</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                A privacidade e segurança dos seus dados são prioridades para o Dashi. Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos suas informações pessoais em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
              </p>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Ao utilizar nossa plataforma, você consente com as práticas descritas nesta política. Recomendamos a leitura atenta deste documento.
              </p>
            </div>

            {/* 2. Dados Coletados */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">2. Dados Coletados</h2>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">2.1 Dados de Cadastro</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Ao criar uma conta no Dashi, coletamos:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground mb-4">
                <li>Nome completo</li>
                <li>Endereço de email</li>
                <li>Nome da empresa (opcional)</li>
                <li>Senha (armazenada de forma criptografada)</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">2.2 Dados de Pagamento</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Para processar pagamentos de planos pagos, utilizamos serviços terceiros confiáveis (como Stripe). Não armazenamos diretamente informações completas de cartão de crédito. Coletamos apenas:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground mb-4">
                <li>Últimos 4 dígitos do cartão</li>
                <li>Bandeira do cartão</li>
                <li>Data de validade</li>
                <li>Histórico de transações</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">2.3 Dados de Uso da Plataforma</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Durante o uso do Dashi, coletamos:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground mb-4">
                <li>Métricas e dados de negócio inseridos ou importados</li>
                <li>Configurações de integrações e webhooks</li>
                <li>Relatórios gerados</li>
                <li>Preferências de personalização</li>
                <li>Logs de atividades na plataforma</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">2.4 Dados Técnicos</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Automaticamente coletamos informações técnicas, incluindo:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground mb-4">
                <li>Endereço IP</li>
                <li>Tipo de navegador e versão</li>
                <li>Sistema operacional</li>
                <li>Páginas visitadas e tempo de permanência</li>
                <li>Data e hora de acesso</li>
                <li>Cookies e tecnologias similares</li>
              </ul>
            </div>

            {/* 3. Como Usamos os Dados */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">3. Como Usamos os Dados</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Utilizamos suas informações pessoais para:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground mb-4">
                <li>Fornecer, operar e manter nossa plataforma</li>
                <li>Processar suas transações e gerenciar sua assinatura</li>
                <li>Personalizar sua experiência na plataforma</li>
                <li>Melhorar e desenvolver novos recursos e funcionalidades</li>
                <li>Comunicar atualizações, mudanças nos serviços e ofertas especiais</li>
                <li>Fornecer suporte ao cliente e responder suas solicitações</li>
                <li>Detectar, prevenir e resolver problemas técnicos ou de segurança</li>
                <li>Realizar análises estatísticas e pesquisas de mercado</li>
                <li>Cumprir obrigações legais e regulatórias</li>
              </ul>
            </div>

            {/* 4. Compartilhamento de Dados */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">4. Compartilhamento de Dados</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Não vendemos, alugamos ou compartilhamos suas informações pessoais com terceiros para fins de marketing. Podemos compartilhar dados apenas nas seguintes situações:
              </p>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">4.1 Provedores de Serviços</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Compartilhamos dados com prestadores de serviços confiáveis que nos auxiliam a operar a plataforma:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground mb-4">
                <li>Processadores de pagamento (Stripe, Hotmart, Mercado Pago, etc.)</li>
                <li>Provedores de infraestrutura em nuvem</li>
                <li>Serviços de análise e monitoramento</li>
                <li>Ferramentas de comunicação e email</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">4.2 Obrigações Legais</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Podemos divulgar suas informações quando exigido por lei, ordem judicial, intimação ou processo legal, ou para proteger direitos, propriedade ou segurança do Dashi, de nossos usuários ou do público.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">4.3 Transferências Empresariais</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Em caso de fusão, aquisição ou venda de ativos, suas informações podem ser transferidas. Notificaremos você sobre qualquer mudança de propriedade ou controle de seus dados pessoais.
              </p>
            </div>

            {/* 5. Armazenamento e Segurança */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">5. Armazenamento e Segurança</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Implementamos medidas de segurança técnicas e organizacionais adequadas para proteger seus dados contra acesso não autorizado, alteração, divulgação ou destruição:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground mb-4">
                <li>Criptografia de dados em trânsito (SSL/TLS) e em repouso</li>
                <li>Autenticação segura com hash de senhas</li>
                <li>Firewalls e sistemas de detecção de intrusão</li>
                <li>Backups regulares e redundância de dados</li>
                <li>Controle de acesso restrito baseado em funções</li>
                <li>Monitoramento contínuo de segurança</li>
                <li>Auditorias periódicas de segurança</li>
              </ul>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Seus dados são armazenados em servidores seguros localizados em datacenters certificados. Apesar de nossos esforços, nenhum sistema é 100% seguro, e não podemos garantir segurança absoluta.
              </p>
            </div>

            {/* 6. Cookies e Tecnologias Similares */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">6. Cookies e Tecnologias Similares</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Utilizamos cookies e tecnologias similares para melhorar sua experiência:
              </p>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">6.1 Tipos de Cookies</h3>
              <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground mb-4">
                <li><strong>Cookies Essenciais:</strong> Necessários para o funcionamento básico da plataforma (autenticação, preferências)</li>
                <li><strong>Cookies Analíticos:</strong> Nos ajudam a entender como os usuários interagem com a plataforma</li>
                <li><strong>Cookies de Funcionalidade:</strong> Lembram suas preferências e configurações</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">6.2 Gerenciamento de Cookies</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Você pode configurar seu navegador para recusar cookies ou alertá-lo quando cookies estão sendo enviados. No entanto, algumas funcionalidades da plataforma podem não funcionar corretamente sem cookies.
              </p>
            </div>

            {/* 7. Direitos do Usuário (LGPD) */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">7. Seus Direitos (LGPD)</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                De acordo com a LGPD, você tem os seguintes direitos em relação aos seus dados pessoais:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground mb-4">
                <li><strong>Confirmação e Acesso:</strong> Confirmar se tratamos seus dados e solicitar acesso a eles</li>
                <li><strong>Correção:</strong> Solicitar a correção de dados incompletos, inexatos ou desatualizados</li>
                <li><strong>Anonimização, Bloqueio ou Eliminação:</strong> Solicitar a anonimização, bloqueio ou eliminação de dados desnecessários</li>
                <li><strong>Portabilidade:</strong> Solicitar a portabilidade de seus dados a outro fornecedor</li>
                <li><strong>Eliminação:</strong> Solicitar a eliminação de dados tratados com seu consentimento</li>
                <li><strong>Informação:</strong> Obter informações sobre entidades públicas e privadas com as quais compartilhamos dados</li>
                <li><strong>Revogação do Consentimento:</strong> Revogar seu consentimento a qualquer momento</li>
                <li><strong>Oposição:</strong> Opor-se ao tratamento realizado em desconformidade com a LGPD</li>
              </ul>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Para exercer qualquer um desses direitos, entre em contato conosco através do email: privacidade@dashi.com.br
              </p>
            </div>

            {/* 8. Retenção de Dados */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">8. Retenção de Dados</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Mantemos seus dados pessoais pelo tempo necessário para:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground mb-4">
                <li>Fornecer nossos serviços enquanto sua conta estiver ativa</li>
                <li>Cumprir obrigações legais, fiscais e contábeis (normalmente 5 anos para dados fiscais)</li>
                <li>Resolver disputas e fazer cumprir nossos acordos</li>
              </ul>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Após o cancelamento da sua conta, manteremos seus dados por até 90 dias para permitir reativação. Após esse período, seus dados serão permanentemente excluídos, exceto aqueles que devemos reter por obrigação legal.
              </p>
            </div>

            {/* 9. Transferência Internacional de Dados */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">9. Transferência Internacional de Dados</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Seus dados podem ser armazenados e processados em servidores localizados fora do Brasil, incluindo em países que podem ter níveis diferentes de proteção de dados. Ao utilizar nossos serviços, você consente com essas transferências.
              </p>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Garantimos que todas as transferências internacionais de dados sejam realizadas com as salvaguardas apropriadas, incluindo cláusulas contratuais padrão e certificações adequadas de nossos provedores.
              </p>
            </div>

            {/* 10. Menores de Idade */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">10. Menores de Idade</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Nossos serviços são destinados a pessoas maiores de 18 anos. Não coletamos intencionalmente informações de menores de idade. Se tomarmos conhecimento de que coletamos dados de um menor sem o consentimento dos pais, tomaremos medidas para excluir essas informações.
              </p>
            </div>

            {/* 11. Alterações na Política */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">11. Alterações nesta Política</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Podemos atualizar esta Política de Privacidade periodicamente para refletir mudanças em nossas práticas ou por outros motivos operacionais, legais ou regulatórios. Notificaremos você sobre alterações significativas por email ou através de um aviso destacado em nossa plataforma.
              </p>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                A data da "Última atualização" no topo deste documento indica quando esta política foi revisada pela última vez. Recomendamos que você revise esta política periodicamente.
              </p>
            </div>

            {/* 12. Contato e Encarregado de Dados */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">12. Contato e Encarregado de Dados</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Se você tiver dúvidas, preocupações ou solicitações relacionadas a esta Política de Privacidade ou ao tratamento de seus dados pessoais, entre em contato conosco:
              </p>
              <div className="bg-muted/50 p-6 rounded-lg space-y-2 my-6">
                <p className="text-muted-foreground"><strong>Email:</strong> privacidade@dashi.com.br</p>
                <p className="text-muted-foreground"><strong>Encarregado de Dados (DPO):</strong> dpo@dashi.com.br</p>
                <p className="text-muted-foreground"><strong>Email Geral:</strong> contato@dashi.com.br</p>
              </div>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Faremos o possível para responder suas solicitações no prazo de 15 dias úteis, conforme estabelecido pela LGPD.
              </p>
            </div>

            {/* Footer */}
            <div className="pt-8 border-t">
              <p className="text-sm text-muted-foreground text-center">
                © {new Date().getFullYear()} Dashi. Todos os direitos reservados.
              </p>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
      <ScrollToTopButton />
    </div>
  );
}
